import { createAsyncThunk } from '@reduxjs/toolkit';
import { loginStart, loginSuccess, loginFailure, logout as logoutAction, setUser } from '../slices/authSlice';
import { resetAll } from '../slices/dataSlice';
import { API_BASE_URL } from 'constants';


// Fetch profile thunk — called after login or hard refresh to hydrate user in Redux
export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const token = localStorage.getItem('auth_token'); 
    // const token = getState().auth?.token || localStorage.getItem('auth_token');
    if (!token) return rejectWithValue('No token');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/admin/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 401) return rejectWithValue('Unauthorized');

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch profile');

      const profile = data.data;
      localStorage.setItem('user_profile', JSON.stringify(profile));
      localStorage.setItem('user_permissions', JSON.stringify(profile.permissions || {}));
      localStorage.setItem('is_super_admin', String(profile.isSuperAdmin));
      localStorage.setItem('user_center_id', profile.centerId || '');
      dispatch(setUser(profile));
      return profile;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Login thunk
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    dispatch(loginStart());

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, deviceType: "web" }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Login failed");
      }

      const token = data.data.token;

      // Store in localStorage
      localStorage.setItem('auth_token', token);
      localStorage.setItem('user_email', email);
      sessionStorage.setItem('isLoggedIn', 'true');

      dispatch(loginSuccess({
        token,
        user: { email, ...data.data.user }
      }));

      dispatch(fetchProfile());

      return data;
    } catch (error) {
      dispatch(loginFailure(error.message));
      return rejectWithValue(error.message);
    }
  }
);

// Logout thunk
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch, getState }) => {
    const { auth } = getState();
    const token = auth.token || localStorage.getItem("auth_token");

    try {
      await fetch(`${API_BASE_URL}/api/v1/auth/admin/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
    } catch (error) {
      console.error('Logout API error:', error);
    }

    // Clear all data and auth state
    dispatch(logoutAction());
    dispatch(resetAll());

    return true;
  }
);

// Signup thunk
export const signupUser = createAsyncThunk(
  'auth/signup',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Forgot password thunk
export const forgotPasswordThunk = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      let data;
      try { data = await response.json(); } catch { data = {}; }

      if (!response.ok) {
        throw new Error(data.message || "Failed to send reset email");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Reset password thunk
export const resetPasswordThunk = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/admin/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Password reset failed");
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
